import { UseFetchResourcesByIdsParams } from "./../../../shared/services/queries/fetchResourcesByIds/types";
import { useState, useMemo, useRef } from "react";
import { useTranslate } from "@refinedev/core";

import { usePaginatedTable, useQueryParams } from "@shared/hooks";
import { ModalRef } from "@shared/components";
import { useFetchResourcesByIdsQuery } from "@shared/services/queries";
import { ApiResponse } from "@shared/types";
import { Agreement } from "@modules/agreements/types";
import { Promocode } from "@modules/promocodes/types";
import { PromocodeBatch } from "@modules/promocodeBatches/types";
import { useQuery } from "@tanstack/react-query";
import { api } from "@api/api";

export function usePromocodesList() {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const createPromocodeModalRef = useRef<ModalRef>(null);
  const editPromocodeModalRef = useRef<ModalRef>(null);

  const translate = useTranslate();
  const { getQueryParams } = useQueryParams();

  const { rows, loading, refetch, ...tableProps } =
    usePaginatedTable<Promocode>({
      resource: "v1/promocode_admin",
      filters: getQueryParams(),
    });

  const batchIds = useMemo(
    () => [
      ...new Set(
        rows
          ?.map((promocode) => promocode.batch)
          .filter((id): id is number => !!id)
      ),
    ],
    [rows]
  );

  const { data: bachs } = useFetchResourcesByIdsQuery<
    ApiResponse<PromocodeBatch>
  >({
    resource: "v1/promocode_batch_admin",
    ids: batchIds,
  });

  const { data: promocodes } = useQuery<any>({
    queryKey: ["v1/promocode_admin/extract-codes", getQueryParams()],
    queryFn: async () => {
      const params = new URLSearchParams(
        getQueryParams() as Record<string, string>
      ).toString();
      const { data: response } = await api.get(
        `/v1/promocode_admin/extract-codes/?${params}`
      );

      return response
    },
  });

  const agreementIds = useMemo(
    () => [
      ...new Set(
        rows
          ?.map((promocode) => promocode.agreement)
          .filter((id): id is number => !!id)
      ),
    ],
    [rows]
  );

  const { data: agreements } = useFetchResourcesByIdsQuery<
    ApiResponse<Agreement>
  >({
    resource: "v1/agreement_admin",
    ids: agreementIds,
  });

  const dataToExport = useMemo(() => {
    if (!promocodes) {
      return [];
    }
    return promocodes.map((el: Promocode) => ({
      codigo: el,
    }));
  }, [promocodes]);

  return {
    rows,
    loading,
    tableProps,
    bachs: bachs?.results,
    agreements: agreements?.results,
    selectedRows,
    dataToExport,
    createPromocodeModalRef,
    editPromocodeModalRef,
    refetch,
    setSelectedRows,
    translate,
  };
}
